import type {
  AiSpreadsheetCellKind,
  AiSpreadsheetValueType,
  IAiSpreadsheetCell,
  IAiSpreadsheetCellLocation,
  IAiSpreadsheetCellOverride,
} from "./cell";
import type {
  IAiSpreadsheetMergedRange,
  IAiSpreadsheetRegion,
  IAiSpreadsheetRegionOverrideReview,
} from "./region";

export interface IAiSpreadsheetSheet {
  id: string;
  ai_spreadsheet_id: string;
  name: string;
  recipient_external_id: string | null;
  access_key: string | null;
  editable: boolean;
  loaded: boolean;
  cell_count: number;
  empty: boolean;
  rerun_scopable: boolean;
  position: number | null;
  column_widths: Record<string, number> | number[] | null;
  row_heights: Record<string, number> | number[] | null;
  frozen_column: number | null;
  frozen_row?: number | null;
  layout_editable?: boolean;
  regions?: IAiSpreadsheetRegion[];
  merged_ranges?: IAiSpreadsheetMergedRange[];
  region_override_reviews?: IAiSpreadsheetRegionOverrideReview[];
  created_at: string;
  updated_at: string;
  cells?: IAiSpreadsheetCell[];
}

export interface IAiSpreadsheetLoadedSheet extends IAiSpreadsheetSheet {
  loaded: true;
  cells: IAiSpreadsheetCell[];
}

export interface IAiSpreadsheet {
  id: string;
  ai_script_run_id?: string;
  profitability_report_run_id?: string;
  business_id: string;
  title: string | null;
  created_at: string;
  updated_at: string;
  sheets: IAiSpreadsheetSheet[];
  region_override_reviews?: IAiSpreadsheetRegionOverrideReview[];
}

export interface IAiSpreadsheetResponse {
  spreadsheet: IAiSpreadsheet;
}

export interface IAiSpreadsheetSheetResponse {
  sheet: IAiSpreadsheetLoadedSheet;
}

export const AI_SPREADSHEET_OPERATION_STATUS = {
  queued: "queued",
  running: "running",
  succeeded: "succeeded",
  failed: "failed",
} as const;

export type AiSpreadsheetOperationStatus =
  (typeof AI_SPREADSHEET_OPERATION_STATUS)[keyof typeof AI_SPREADSHEET_OPERATION_STATUS];

export interface AiSpreadsheetOperationResult {
  version?: number;
  changed_cell_ids?: string[];
  changed_sheet_ids?: string[];
  manual_row_ids?: string[];
  deleted_cell_ids?: string[];
  override_id?: string;
}

export interface AiSpreadsheetOperation {
  id: string;
  operation_type: string;
  status: AiSpreadsheetOperationStatus;
  spreadsheet_id: string;
  ai_script_run_id: string;
  idempotency_key: string;
  result: AiSpreadsheetOperationResult | null;
  error: { type: string; message: string } | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
}

export interface AcceptedAiSpreadsheetOperationResponse {
  operation: AiSpreadsheetOperation;
  idempotent_replay: boolean;
}

export interface IAiSpreadsheetPasteEdit {
  cell_id: string;
  raw_input: string;
  kind?: AiSpreadsheetCellKind;
  value_type?: AiSpreadsheetValueType;
}

export interface IAiScriptCellOverridesResponse {
  overrides: IAiSpreadsheetCellOverride[];
}

export interface IAcceptAiScriptCellOverrideResponse {
  override: IAiSpreadsheetCellOverride;
}

export interface IAiSpreadsheetOverrideCandidate
  extends IAiSpreadsheetCellLocation {
  cell_id: string;
  value: string | number | boolean | null;
}

export interface IAiScriptOverrideCandidatesResponse {
  candidates: IAiSpreadsheetOverrideCandidate[];
}

export interface IUpdateAiSpreadsheetSheetResponse {
  sheet: Pick<
    IAiSpreadsheetSheet,
    "id" | "column_widths" | "row_heights" | "frozen_column" | "frozen_row"
  >;
}
